import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography
} from '@mui/material';
import { emptyStateSx, tableSx, headerCellSx, headerRowSx, bodyRowSx, bodyCellSx } from './styles';

export interface UsageReportRow {
  user_id: string;
  username?: string;
  email?: string;
  organization: string;
  last_used_date: string;
  total_time_spent_hours: number;
  /** Per-user RUM session counts from Datadog */
  total_view_count?: number;
  total_action_count?: number;
  /** Per-user stats from user-stats API */
  aria_search_count?: number;
  advance_search_count?: number;
  quick_search_count?: number;
  chat_ria_search_count?: number;
}

interface ReportTableProps {
  data: UsageReportRow[];
  /** When true (e.g. organization is Novo Nordisk), Username and Email columns are shown */
  showUsernameEmail?: boolean;
}

/** Fixed stat column labels (Search Stats = sum of Aria/Advance/Quick, Chat RIA Stats). */
const STAT_COLUMN_LABELS = ['Total Search Stats', 'Total Chat RIA Stats'];

const ReportTable: React.FC<ReportTableProps> = ({ data, showUsernameEmail = false }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (data.length === 0) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant='body1' color='textSecondary'>
          No data found. Please adjust your filters.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table sx={tableSx}>
        <TableHead>
          <TableRow sx={headerRowSx}>
            <TableCell sx={headerCellSx}>User ID</TableCell>
            {showUsernameEmail && (
              <>
                <TableCell sx={headerCellSx}>Username</TableCell>
                <TableCell sx={headerCellSx}>Email</TableCell>
              </>
            )}
            <TableCell sx={headerCellSx}>Organization</TableCell>
            <TableCell sx={headerCellSx}>Last Used Date</TableCell>
            <TableCell align='right' sx={headerCellSx}>
              Total Time Spent (hrs)
            </TableCell>
            <TableCell align='right' sx={headerCellSx}>
              View Count
            </TableCell>
            <TableCell align='right' sx={headerCellSx}>
              Action Count
            </TableCell>
            {STAT_COLUMN_LABELS.map(label => (
              <TableCell key={label} align='right' sx={headerCellSx}>
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(row => (
            <TableRow key={row.user_id} sx={bodyRowSx}>
              <TableCell sx={bodyCellSx}>{row.user_id}</TableCell>
              {showUsernameEmail && (
                <>
                  <TableCell sx={bodyCellSx}>{row.username ?? '—'}</TableCell>
                  <TableCell sx={bodyCellSx}>{row.email ?? '—'}</TableCell>
                </>
              )}
              <TableCell sx={bodyCellSx}>{row.organization}</TableCell>
              <TableCell sx={bodyCellSx}>{formatDate(row.last_used_date)}</TableCell>
              <TableCell align='right' sx={bodyCellSx}>
                {row.total_time_spent_hours.toFixed(2)}
              </TableCell>
              <TableCell align='right' sx={bodyCellSx}>
                {(row.total_view_count ?? 0).toLocaleString()}
              </TableCell>
              <TableCell align='right' sx={bodyCellSx}>
                {(row.total_action_count ?? 0).toLocaleString()}
              </TableCell>
              <TableCell align='right' sx={bodyCellSx}>
                {(() => {
                  const aria = row.aria_search_count ?? 0;
                  const advance = row.advance_search_count ?? 0;
                  const quick = row.quick_search_count ?? 0;
                  const total = aria + advance + quick;
                  return total > 0 ? total.toLocaleString() : '—';
                })()}
              </TableCell>
              <TableCell align='right' sx={bodyCellSx}>
                {row.chat_ria_search_count != null
                  ? row.chat_ria_search_count.toLocaleString()
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ReportTable.defaultProps = {
  showUsernameEmail: false
};

export default ReportTable;
